/* La Sura - the running price on the product page's Add to basket (2026-09-30, product page
   restyle part 3). What checkout will charge for this line: the selected size's price (its
   subscription price when Subscribe and save is picked, 2026-10-27), plus each ticked paid
   extra (added under the cake at the cake's quantity by lasura-cake-extras.js), times the
   quantity. Formatted with Horizon's own money formatter,
   which follows the shop's money format. The server renders the size's price first, so
   without this script the button still shows a correct price for one. Each paint leaves the
   total in pence on data-total and fires lasura:buy-price, which the free delivery line reads
   (assets/lasura-free-line.js, redesign L7).

   The price under the title and the Serves chip follow the paid add-ons too (Matt, 2026-10-30): a
   two-tier 8 inch reads "£100 with two tiers" and "Serves 26 to 34". The page's own price and serves
   stay the size's, and come back when the add-ons are taken off. */
import { formatMoney } from '@theme/money-formatting';
import { addOnLines } from '@theme/lasura-ticket-lines';

/* Whole pounds read without pence (cake page v2): "£65", as the Liquid's money_without_trailing_zeros. */
const money = (cents, format, currency) => {
  const text = formatMoney(cents, format, currency);
  return cents % 100 === 0 ? text.replace(/[.,]00(?!\d)/, '') : text;
};

/* Subscribe picked: the plan's price for one box, from the purchase options block tied to this
   form (lasura-purchase-options.js), as a selling plan radio doesn't carry a price. Null on Buy once. */
const planPrice = (form) => {
  const plan = [...form.elements].find((field) => field.name === 'selling_plan' && field.checked && field.value);
  /* Not form.id: the form's input named "id" (the variant) shadows it. */
  const formId = form.getAttribute('id');
  if (!plan || !formId) return null;
  const options = document.querySelector(`lasura-purchase-options[data-form-id="${CSS.escape(formId)}"]`);
  return options?.planPrice?.(plan.value) ?? null;
};

/* The price under the title: the size's price plus the ticked add-ons, for one cake, worded by the
   order ticket's "with {extras}" (blank keeps the size's price). Horizon redraws it on a size change. */
const headPrice = (form, unit, el) => {
  const formId = form.getAttribute('id');
  const summary = formId && document.querySelector(`.lasura-summary[data-form="${CSS.escape(formId)}"]`);
  const price = form.closest('.product-details')?.querySelector('product-price .price__regular:not(.price__hidden) .price');
  if (!summary?.dataset.priceWith || !price) return;
  price.dataset.lasuraBase ??= price.textContent;
  const names = addOnLines(form, { upgrade: summary.dataset.upgrade }).map((line) => line.name.charAt(0).toLowerCase() + line.name.slice(1));
  const text = names.length ? money(unit, el.dataset.format, el.dataset.currency) : price.dataset.lasuraBase;
  if (price.textContent !== text) price.textContent = text;
  let note = price.parentElement.querySelector('.lasura-price-with');
  if (!names.length) return note?.remove();
  if (!note) {
    note = document.createElement('span');
    note.className = 'lasura-price-with';
    price.after(note);
  }
  const words = summary.dataset.priceWith.replace('{extras}', names.join(', '));
  if (note.textContent !== words) note.textContent = words;
};

/* The Serves chip: the size's serves plus the picked choices' extra servings (a tier's), and each such
   group's line: "Feeds 10 to 12 more, about 26 to 34 in all." */
const span = (lo, hi, range) => (lo > 0 && hi > lo ? range.replace('{min}', lo).replace('{max}', hi) : String(hi));
const headServes = (form) => {
  const chip = form.closest('.product-information')?.querySelector('[data-lasura-serves]');
  if (!chip) return;
  const [lo, hi] = chip.dataset.lasuraServes.split('|').map(Number);
  const formId = form.getAttribute('id');
  const fields = [...form.elements, ...(formId ? document.querySelectorAll(`input[data-extras-for="${CSS.escape(formId)}"]`) : [])];
  let addLo = 0;
  let addHi = 0;
  const lines = new Map([...document.querySelectorAll('[data-lasura-serves-more]')].map((line) => [line, '']));
  for (const field of fields) {
    const add = field.checked && !field.disabled && field.closest('[data-serves-add]');
    if (!add) continue;
    const [a, b] = add.dataset.servesAdd.split('|').map(Number);
    addLo += a || 0;
    addHi += b || a || 0;
    const line = add.closest('fieldset')?.querySelector('[data-lasura-serves-more]');
    if (line) lines.set(line, [a || 0, b || a || 0]);
  }
  /* The page's own words when nothing adds servings, so its wording ("Serves up to 5") stands. */
  chip.dataset.lasuraBase ??= chip.textContent;
  const text = addHi > 0 && hi > 0 ? chip.dataset.chip.replace('{serves}', span(lo + addLo, hi + addHi, chip.dataset.range)) : chip.dataset.lasuraBase;
  if (chip.textContent !== text) chip.textContent = text;
  for (const [line, add] of lines) {
    const words = add && hi > 0
      ? line.dataset.lasuraServesMore.replace('{more}', span(add[0], add[1], line.dataset.range)).replace('{total}', span(lo + addLo, hi + addHi, chip.dataset.range))
      : '';
    if (line.textContent !== words) line.textContent = words;
  }
};

const paint = () => {
  for (const el of document.querySelectorAll('[data-lasura-buy-price]')) {
    /* The bottom bar sits outside the form, so it names the form it totals. */
    const form = el.closest('form') || document.getElementById(el.dataset.lasuraBuyForm || '');
    if (!form) continue;
    const size = Number(el.dataset.price) || 0;
    let extras = 0;
    for (const field of form.elements) {
      const extra = field.checked && !field.disabled && field.closest('[data-add-on-price]');
      if (extra) extras += Number(extra.dataset.addOnPrice) || 0;
    }
    /* Choice group boxes (Matching cupcakes, redesign L1.4) stay out of the form so nothing extra
       posts with the cake; they name it with data-extras-for instead. */
    const formId = form.getAttribute('id');
    if (formId) {
      for (const field of document.querySelectorAll(`input[data-extras-for="${CSS.escape(formId)}"]:checked:not(:disabled)`)) {
        const extra = field.closest('[data-add-on-price]');
        if (extra) extras += Number(extra.dataset.addOnPrice) || 0;
      }
    }
    const unit = (planPrice(form) ?? size) + extras;
    /* Once per product: from the button in the form, not the bottom bar that names it. */
    if (el.closest('form')) {
      headPrice(form, size + extras, el);
      headServes(form);
    }
    const quantity = Math.max(1, Math.floor(Number(form.elements.quantity?.value) || 1));
    const amount = el.querySelector('[data-lasura-buy-amount]');
    const text = money(unit * quantity, el.dataset.format, el.dataset.currency);
    if (amount && amount.textContent !== text) amount.textContent = text;
    /* The total in pence, for the free delivery line (lasura-free-line.js, redesign L7). */
    el.dataset.total = String(unit * quantity);
  }
  document.dispatchEvent(new CustomEvent('lasura:buy-price'));
};

document.addEventListener('variant:update', (event) => {
  const price = event.detail?.resource?.price;
  const productId = String(event.detail?.data?.productId || '');
  /* Horizon may redraw the buttons for the new size; set the price after it has. */
  setTimeout(() => {
    for (const el of document.querySelectorAll('[data-lasura-buy-price]')) {
      const own = el.closest('form')?.querySelector('input[name="product-id"]')?.value;
      if (price != null && (!productId || !own || own === productId)) el.dataset.price = String(price);
    }
    paint();
  }, 0);
});
['change', 'input'].forEach((type) => document.addEventListener(type, paint, true));
/* A plan ticked before the purchase options block is ready is priced once it is. */
customElements.whenDefined('lasura-purchase-options').then(paint);
paint();
